import * as Sentry from '@sentry/react';
import { ErrorPhrase } from '@constants/common';
import { pages } from '@constants/pages';
import { TLogErrorOptions } from '@models/common';
import { getAttemptDuration, SLOW_FAILURE_MS } from '@utils/attemptDuration';
import { isEnvDevelopment } from '@utils/common';

const MAX_LOG_STRING_LENGTH = 500;
const FAILED_FETCH_REGEX = /failed to fetch|networkerror|load failed/i;
/** `buildFailureMessage` in `api/wr-tools/shared/request.ts` when no attempt got a response. */
const NO_RESPONSE_STATUS_REGEX = /request failed with status 0\./i;
/** The same helper's message for any status — a technical dump, not for the user. */
const WR_TOOLS_STATUS_ERROR_REGEX = /request failed with status (\d+)/i;
/** `errorType` of a request answered with a non-success `status` in its body. */
export const RESPONSE_STATUS_ERROR_TYPE = 'response_status';
const MIN_SERVER_ERROR_STATUS = 500;
// Matches webpack's `Loading chunk <id> failed.` (and the CSS variant).
const CHUNK_LOAD_REGEX = /loading (css )?chunk .*failed/i;
/** The chunk URL in webpack's message: `Loading chunk 1 failed.\n(error: <url>)`. */
const CHUNK_URL_REGEX = /https?:\/\/\S+?\.js/;
const CHUNK_ERROR_RELOADED_KEY = 'chunkErrorPageReloadedAt';
const CHUNK_ERROR_REPORTED_KEY = 'chunkErrorReportedAt';
const SCHEMA_MISMATCH_RELOADED_KEY = 'schemaMismatchPageReloadedAt';
/**
 * How long a reload suppresses the next one. Long enough that a failure still
 * present after the reload cannot start a loop, short enough that an unrelated
 * failure later in the same tab still gets its own recovery attempt.
 */
const RELOAD_COOLDOWN_MS = 60_000;
/**
 * graphql-php validation messages for an operation the deployed schema no
 * longer accepts: an argument or field was renamed/removed, or a required
 * argument was added. The request never reaches a resolver.
 */
const GRAPHQL_SCHEMA_MISMATCH_REGEX =
  /unknown argument|cannot query field|unknown type|argument "[^"]+" of type "[^"]+" is required/i;
/**
 * Outcomes the UI already handles as a regular state, not failures —
 * `logError` keeps them out of Sentry. Each pattern matches the exact message,
 * so an unrelated error is never dropped by accident.
 */
const EXPECTED_ERROR_PATTERNS: RegExp[] = [
  // The humanizer returned the text as is; the form shows a low-score hint.
  /text stayed unchanged after humanization/i,
];

const getErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message;
  }

  return String(error);
};

const getTruncatedString = (value: string): string =>
  value.length > MAX_LOG_STRING_LENGTH
    ? `${value.slice(0, MAX_LOG_STRING_LENGTH)}...[truncated] (length: ${
        value.length
      })`
    : value;

const sanitizeLogValue = (
  value: unknown,
  visited: WeakSet<object> = new WeakSet()
): unknown => {
  if (typeof value === 'string') {
    return getTruncatedString(value);
  }

  if (Array.isArray(value)) {
    return value.map((arrayValue) => sanitizeLogValue(arrayValue, visited));
  }

  if (value && typeof value === 'object') {
    if (visited.has(value)) {
      return '[Circular]';
    }

    visited.add(value);

    return Object.entries(value).reduce<Record<string, unknown>>(
      (acc, [key, nestedValue]) => {
        acc[key] = sanitizeLogValue(nestedValue, visited);
        return acc;
      },
      {}
    );
  }

  return value;
};

export const isFailedFetchError = (error: unknown): boolean =>
  FAILED_FETCH_REGEX.test(getErrorMessage(error));

/** An error from `EXPECTED_ERROR_PATTERNS` — not reported to Sentry. */
export const isExpectedError = (error: unknown): boolean =>
  EXPECTED_ERROR_PATTERNS.some((pattern) =>
    pattern.test(getErrorMessage(error))
  );

/**
 * A request that got no response at all: the browser's own fetch error, or a
 * writing-tools REST call that `requestWrToolsText` already retried and
 * reports as status 0. Used for logging and user messages only — the retry
 * policy keeps `isFailedFetchError`, so REST calls are not retried on top of
 * their own attempts.
 */
const isNoResponseError = (error: unknown): boolean =>
  isFailedFetchError(error) ||
  NO_RESPONSE_STATUS_REGEX.test(getErrorMessage(error));

/**
 * Maps a raw mutation/query error (graphql-request ClientError, network error)
 * to a friendly, localized message — never the raw serialized dump that
 * graphql-request puts in `ClientError.message`. A string is a message the
 * caller has already prepared and is returned as is.
 */
export const getReadableErrorMessage = (
  error: unknown,
  formatMessage: (descriptor: { id: string }) => string
): string => {
  if (!error) {
    return '';
  }

  if (typeof error === 'string') {
    return error;
  }

  const id = isFailedFetchError(error)
    ? ErrorPhrase.REQUEST_FAILED
    : ErrorPhrase.BASE_ERROR;

  return formatMessage({ id });
};

/**
 * The message a /tools form shows for a failed request. A message the backend
 * wrote for the user (`n1_error`, `n3_error`, …) is kept; the technical ones —
 * a connection with no response, the `"… request failed with status N"` dump
 * from `requestWrToolsText` — are replaced with a localized phrase.
 */
export const getToolErrorMessage = (
  error: unknown,
  formatMessage: (descriptor: { id: string }) => string
): string => {
  if (!error) {
    return '';
  }

  if (isNoResponseError(error)) {
    return formatMessage({ id: ErrorPhrase.REQUEST_FAILED });
  }

  const message =
    typeof error === 'string' || error instanceof Error
      ? getErrorMessage(error)
      : '';

  if (!message || WR_TOOLS_STATUS_ERROR_REGEX.test(message)) {
    return formatMessage({ id: ErrorPhrase.BASE_ERROR });
  }

  return message;
};

/** The HTTP status in a `requestWrToolsText` error; `undefined` for any other error. */
const getWrToolsStatus = (error: unknown): string | undefined =>
  WR_TOOLS_STATUS_ERROR_REGEX.exec(getErrorMessage(error))?.[1];

/** The `status` a request carried in its body (`useHandleErrors.handleErrorsOnSuccess`). */
const getResponseStatus = (options?: TLogErrorOptions): number | undefined =>
  options?.errorType === RESPONSE_STATUS_ERROR_TYPE &&
  typeof options.statusCode === 'number'
    ? options.statusCode
    : undefined;

const getNetworkContext = () => ({
  isOnline:
    typeof navigator !== 'undefined' ? navigator.onLine : 'not_available',
  url: typeof window !== 'undefined' ? window.location.href : 'not_available',
});

/**
 * A stale client requesting a chunk hash from a previous deploy. Webpack sets
 * `name` to 'ChunkLoadError'; the message check covers the '(missing: …)'
 * variants and errors that lost their prototype crossing a boundary.
 */
export const isChunkLoadError = (error: unknown): boolean => {
  if (error instanceof Error && error.name === 'ChunkLoadError') {
    return true;
  }

  return CHUNK_LOAD_REGEX.test(getErrorMessage(error));
};

/**
 * True once THIS page-load has scheduled a reload. `window.location.reload()`
 * is async — the current execution keeps running until the navigation lands, so
 * every other chunk that fails in the same burst (a route pulls several lazy
 * chunks, and both the `unhandledrejection` and capture-phase `error` listeners
 * catch the same failure) would otherwise see the just-set timestamp and be
 * misreported as "reload didn't help". They are siblings of the same failure,
 * not a post-reload failure — ignore them; the scheduled reload will recover.
 */
let hasScheduledReload = false;

/** True once this page-load has scheduled a reload — errors after it are moot. */
export const isPageReloadScheduled = (): boolean => hasScheduledReload;

/**
 * Reloads the page unless a reload for the same cause (`reloadedKey`) already
 * happened within the cooldown. Returns true when a reload is scheduled (now or
 * earlier in this page-load); false means the reload did not help.
 */
const reloadOnce = (reloadedKey: string): boolean => {
  if (hasScheduledReload) {
    return true;
  }

  const reloadedAt = Number(sessionStorage.getItem(reloadedKey));
  const isWithinCooldown =
    Boolean(reloadedAt) && Date.now() - reloadedAt < RELOAD_COOLDOWN_MS;

  if (isWithinCooldown) {
    return false;
  }

  hasScheduledReload = true;
  sessionStorage.setItem(reloadedKey, String(Date.now()));
  window.location.reload();
  return true;
};

/** Webpack puts the chunk URL on `ChunkLoadError.request`; fall back to the message. */
const getChunkUrl = (error: Error): string | undefined => {
  const { request } = error as Error & { request?: unknown };

  if (typeof request === 'string' && request) {
    return request;
  }

  return CHUNK_URL_REGEX.exec(error.message)?.[0];
};

/**
 * Requests the failed chunk once more, outside webpack. A request that fails
 * too means the browser cannot reach the CDN right now (a dropped mobile
 * connection, WebKit's `Load failed`, a VPN or content blocker) — the chunk is
 * there, nothing to fix on our side. A response (opaque: the CDN is another
 * origin) means the server answered and the chunk is really unavailable.
 */
const isChunkUnreachable = async (url: string): Promise<boolean> => {
  try {
    await fetch(url, { mode: 'no-cors', cache: 'no-store' });
    return false;
  } catch {
    return true;
  }
};

const reportUnrecoverableChunkError = (error: Error): void => {
  Sentry.captureException(
    new Error(`${error.name}. Page is already reloaded`),
    {
      contexts: {
        payload: {
          error,
        },
      },
    }
  );
};

export const reloadOnChunkError = (error: Error) => {
  if (reloadOnce(CHUNK_ERROR_RELOADED_KEY)) {
    return;
  }

  // A fresh page-load (no reload scheduled here) that is still within the
  // cooldown of a previous reload: the reload genuinely happened and the chunk
  // is STILL missing (stale index.html / CDN cache / an incomplete deploy) — a
  // truly unrecoverable stale client. Report once per reload window so a page
  // with several dead chunks does not multiply this event in Sentry.
  const reloadedAt = Number(sessionStorage.getItem(CHUNK_ERROR_RELOADED_KEY));
  const reportedAt = Number(sessionStorage.getItem(CHUNK_ERROR_REPORTED_KEY));
  const alreadyReported = Boolean(reportedAt) && reportedAt >= reloadedAt;

  if (alreadyReported) {
    return;
  }

  sessionStorage.setItem(CHUNK_ERROR_REPORTED_KEY, String(Date.now()));

  const chunkUrl = getChunkUrl(error);

  if (!chunkUrl) {
    reportUnrecoverableChunkError(error);
    return;
  }

  // `isChunkUnreachable` never rejects, so the promise needs no `.catch`.
  isChunkUnreachable(chunkUrl).then((isUnreachable) => {
    if (!isUnreachable) {
      reportUnrecoverableChunkError(error);
    }
  });
};

/**
 * The server rejected the operation itself (see
 * `GRAPHQL_SCHEMA_MISMATCH_REGEX`): a tab opened before a deploy still sends
 * the old query to the new schema.
 */
export const isGraphQLSchemaMismatchError = (error: unknown): boolean => {
  // graphql-request's `ClientError` shape, checked structurally: importing the
  // class pulls an ESM-only package into every Jest suite that uses this file.
  const errors = (error as { response?: { errors?: unknown } } | null)?.response
    ?.errors;

  return (
    Array.isArray(errors) &&
    errors.some(
      (graphQLError) =>
        typeof graphQLError?.message === 'string' &&
        GRAPHQL_SCHEMA_MISMATCH_REGEX.test(graphQLError.message)
    )
  );
};

/**
 * Reloads a stale tab once so it picks up the bundle that matches the deployed
 * schema. Returns true when the reload takes over; false when the error is
 * something else or the reload already happened and did not help — handle it as
 * a regular error then.
 */
export const reloadOnGraphQLSchemaMismatch = (error: unknown): boolean =>
  isGraphQLSchemaMismatchError(error) &&
  reloadOnce(SCHEMA_MISMATCH_RELOADED_KEY);

const handleChunkRejection = (event: PromiseRejectionEvent): void => {
  const { reason } = event;

  if (!isChunkLoadError(reason)) {
    return;
  }

  // Stop the SDK's global handler from also reporting it as an unhandled
  // rejection — recovery is a reload, not a Sentry event.
  event.preventDefault();
  reloadOnChunkError(
    reason instanceof Error ? reason : new Error(String(reason))
  );
};

let isChunkErrorRecoveryInitialized = false;

/**
 * Global safety net for chunk-load failures that escape the React error
 * boundary and the router `errorElement`: they surface as an unhandled promise
 * rejection and would otherwise leave a stale tab broken with no reload
 * attempt. Routes them into the same `reloadOnChunkError` recovery. Register
 * once at bootstrap.
 *
 * There is deliberately no listener for the failing `<script>` element's
 * `error` event: it fires before webpack rejects the `import()`, so reloading
 * from there would cut `retryImport` short — and its retries are what recovers
 * a transient network failure. Every chunk failure still rejects its
 * `import()` and reaches the recovery above.
 */
export const initChunkErrorRecovery = (): void => {
  if (isChunkErrorRecoveryInitialized || typeof window === 'undefined') {
    return;
  }

  isChunkErrorRecoveryInitialized = true;
  window.addEventListener('unhandledrejection', handleChunkRejection);
};

type PageArea = 'tools' | 'profile' | 'site';

const isUnderPath = (pathname: string, path: string): boolean =>
  pathname === path || pathname.startsWith(`${path}/`);

/** The cabinet areas (`/tools`, the profile) are grouped apart from the site. */
const getPageArea = (): PageArea => {
  if (typeof window === 'undefined') {
    return 'site';
  }

  const { pathname } = window.location;

  if (isUnderPath(pathname, pages.tools.mainPage)) {
    return 'tools';
  }

  if (
    Object.values(pages.userProfile).some((path) => isUnderPath(pathname, path))
  ) {
    return 'profile';
  }

  return 'site';
};

/** `operation` from the query hooks, `location`/`context` from manual calls. */
const getLogOperation = (options?: TLogErrorOptions): string | undefined => {
  const operation = options?.operation ?? options?.location ?? options?.context;

  return typeof operation === 'string' ? operation : undefined;
};

export const logError = (error: unknown, options?: TLogErrorOptions): void => {
  const errorMessage = typeof error === 'string' ? error : String(error);
  const errorInstance =
    error instanceof Error ? error : new Error(errorMessage);

  if (isEnvDevelopment()) {
    console.error(errorInstance, options);
    return;
  }

  if (isExpectedError(error)) {
    return;
  }

  const sanitizedOptions = sanitizeLogValue(options);
  const payloadContext =
    sanitizedOptions &&
    typeof sanitizedOptions === 'object' &&
    !Array.isArray(sanitizedOptions)
      ? (sanitizedOptions as Record<string, unknown>)
      : {};

  // A failed fetch carries no stack and no request info, so without this tag
  // the event cannot be traced back to the operation that failed.
  const operation = getLogOperation(options);

  if (isNoResponseError(errorInstance)) {
    const attemptDurationMs = getAttemptDuration(error);
    const isSlow =
      attemptDurationMs !== undefined && attemptDurationMs >= SLOW_FAILURE_MS;

    Sentry.withScope((scope) => {
      if (operation) {
        scope.setTag('operation', operation);
      }

      scope.setTag('area', getPageArea());

      // `slow` — the request hung until something on the way cut it off (a
      // server-side timeout); `fast` — the connection never got through.
      if (attemptDurationMs !== undefined) {
        scope.setTag('failure_speed', isSlow ? 'slow' : 'fast');
      }

      // Two issues in all: a server-side timeout, which is worth a look, and
      // the user's own network (an untimed request counts here too). The
      // operation and the area stay tags, so a new request or a new release
      // does not open another issue.
      scope.setFingerprint(['failed-fetch', isSlow ? 'slow' : 'no-response']);

      scope.setLevel('warning');
      scope.setTag('error_kind', 'failed_fetch');
      scope.setContext('network', {
        ...getNetworkContext(),
        attemptDurationMs,
      });
      scope.setContext('payload', {
        ...payloadContext,
        errorType: 'failed_fetch',
      });
      Sentry.captureException(errorInstance);
    });

    return;
  }

  const wrToolsStatus = getWrToolsStatus(errorInstance);
  const responseStatus = getResponseStatus(options);
  const statusCode =
    wrToolsStatus ??
    (responseStatus === undefined ? undefined : String(responseStatus));
  const tags = {
    ...(operation && { operation }),
    ...(statusCode && { status_code: statusCode }),
  };

  // One issue per operation and status: a backend 500 and a validation 400
  // go to different owners, and the response body in the message no longer
  // splits or merges them. A body status otherwise lands in one issue for
  // every operation: they all throw from the same handler.
  const getFingerprint = () => {
    if (wrToolsStatus) {
      return ['wr-tools-status', operation ?? '{{ default }}', wrToolsStatus];
    }

    if (statusCode) {
      return ['response-status', operation ?? '{{ default }}', statusCode];
    }

    return undefined;
  };

  // A 4xx in the body is the backend refusing on business rules (no words
  // left, no session), not a fault: still tracked, but below the errors.
  const isBusinessRefusal =
    responseStatus !== undefined && responseStatus < MIN_SERVER_ERROR_STATUS;

  Sentry.captureException(errorInstance, {
    tags: Object.keys(tags).length ? tags : undefined,
    fingerprint: getFingerprint(),
    ...(isBusinessRefusal && { level: 'warning' as const }),
    contexts: {
      payload: payloadContext,
    },
  });
};
